/**
 * Phase 18.16-B Batch 1 — Generic CMS-driven service-page wrapper.
 *
 * Renders a full premium service page (hero + intro + cards + cta band)
 * by fetching:
 *   GET /api/website/service-pages/{slug}
 *
 * Each consumer page (SeaFreightPage, AirFreightPage, etc.) just passes
 * its slug + a production-matching `fallback` prop. Below the CMS-driven
 * intro/cards/cta-band, the existing route content (such as the legacy
 * QuoteForm block on /sea-freight) is preserved via the `extras`
 * children slot so we don't break the conversion path.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowRight, Sparkles, Ship, Plane, Package, Truck, Car, MapPin,
  Briefcase, User, CheckCircle2, Clock, ShieldCheck, Banknote,
  Headphones, Info, Receipt, Zap,
} from "lucide-react";
import Schema from "./Schema";
import Breadcrumbs from "./Breadcrumbs";
import RelatedDestinations from "./RelatedDestinations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ORIGIN = (process.env.REACT_APP_PUBLIC_ORIGIN
  || "https://www.5999cargo.nl").replace(/\/$/, "");

// SEO Sprint 2 — slug → human name + breadcrumb label.
// `service-pages` factory uses these slugs as both URL and CMS key.
const SERVICE_META = {
  "sea-freight":     { name: "Zeevracht",      url_path: "/sea-freight" },
  "air-freight":     { name: "Luchtvracht",    url_path: "/air-freight" },
  "moving":          { name: "Verhuizing",     url_path: "/moving" },
  "shop-drop-ship":  { name: "Shop, Drop & Ship", url_path: "/shop-drop-ship" },
  "business":        { name: "Zakelijke zendingen", url_path: "/zakelijk" },
};

// SEO Sprint 3 — internal-linking config per service.
// Each entry decides whether (and how) to show the "Andere bestemmingen"
// strip with a service-specific anchor template.
const SERVICE_RELATED = {
  "sea-freight": {
    eyebrow: "Zeevracht per bestemming",
    title: "Zeevracht naar de Caribbean",
    description: "Bekijk hoe wij zeevracht verzorgen naar onze 5 belangrijkste Caribbean-bestemmingen — groupage of complete container, met begeleiding van paklijst tot lokale aflevering.",
    anchorTemplate: "Zeevracht naar {name}",
  },
  "air-freight": {
    eyebrow: "Luchtvracht per bestemming",
    title: "Luchtvracht naar de Caribbean",
    description: "Voor pakketten, spoedonderdelen en tijdkritische zendingen — bekijk wat wij per bestemming verzorgen.",
    anchorTemplate: "Luchtvracht naar {name}",
  },
  "shop-drop-ship": {
    eyebrow: "Shop, Drop & Ship per bestemming",
    title: "Online shoppen en doorsturen",
    description: "Laat uw online aankopen leveren op ons adres en wij sturen ze door naar uw bestemming.",
    anchorTemplate: "Shop, Drop & Ship naar {name}",
  },
};

const ICON_REGISTRY = {
  ship: Ship, plane: Plane, package: Package, truck: Truck, car: Car,
  map: MapPin, briefcase: Briefcase, user: User,
  check: CheckCircle2, clock: Clock, shield: ShieldCheck,
  banknote: Banknote, support: Headphones, sparkles: Sparkles,
  info: Info, receipt: Receipt, zap: Zap,
};
const resolveIcon = (k) => ICON_REGISTRY[String(k || "").toLowerCase()] || Package;

const CARD_GRADIENT = "from-[#082c50] via-[#1a5193] to-[#194d8d]";

export default function ServicePageCmsWrapper({
  slug,
  fallback,           // { page_banner, intro, cta_band, cards }
  children,           // optional JSX rendered between intro/cards and cta_band
  bottomChildren,     // optional JSX rendered AFTER cta_band (e.g. legacy quote section)
  testid,
}) {
  const [data, setData] = useState(fallback || null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/website/service-pages/${slug}`, { timeout: 8000 })
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.sections) {
          setData({
            page_banner: { ...(fallback?.page_banner || {}), ...(res.data.sections.page_banner || {}) },
            intro:       { ...(fallback?.intro || {}),       ...(res.data.sections.intro || {}) },
            cta_band:    { ...(fallback?.cta_band || {}),    ...(res.data.sections.cta_band || {}) },
            cards:       Array.isArray(res.data.cards) && res.data.cards.length > 0
              ? res.data.cards
              : (fallback?.cards || []),
          });
        }
      })
      .catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, [slug]);

  if (!data) return null;
  const banner = data.page_banner || {};
  const intro = data.intro || {};
  const cta = data.cta_band || {};
  const cards = data.cards || [];

  // SEO Sprint 2 — Service schema + Breadcrumb schema/UI per service page.
  const meta = SERVICE_META[slug];
  const serviceSchemaData = meta ? {
    name: meta.name,
    description: banner.subtitle || `Dienst ${meta.name} — 5999Cargo Group Europe.`,
    serviceType: meta.name,
    url: `${ORIGIN}${meta.url_path}`,
  } : null;
  const breadcrumbs = meta ? [
    { name: "Home", url: "/" },
    { name: "Diensten", url: "/" },
    { name: meta.name },
  ] : null;

  return (
    <div data-testid={testid || `service-page-${slug}`}>
      {serviceSchemaData ? <Schema type="Service" data={serviceSchemaData} /> : null}
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-[#082c50] text-white" data-testid={`${slug}-hero`}>
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#082c50] via-[#0a335c] to-[#194d8d]" />
          <div className="absolute -top-32 -left-24 w-[36rem] h-[36rem] rounded-full bg-cyan-400/10 blur-[120px]" />
          <div className="absolute top-32 -right-32 w-[40rem] h-[40rem] rounded-full bg-[#1a5193]/40 blur-[140px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            {banner.eyebrow ? (
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-[11px] tracking-[0.18em] uppercase font-chivo font-bold">
                <Sparkles className="w-3.5 h-3.5 text-cyan-300" strokeWidth={2} />
                {banner.eyebrow}
              </span>
            ) : null}
            <h1 className="font-chivo font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.04] mt-5">
              {banner.title}
              {banner.highlight ? (
                <> {" "}
                  <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-white bg-clip-text text-transparent">
                    {banner.highlight}
                  </span>
                </>
              ) : null}
            </h1>
            {banner.subtitle ? (
              <p className="mt-5 text-white/85 max-w-2xl text-lg leading-relaxed">{banner.subtitle}</p>
            ) : null}
            {Array.isArray(banner.badges) && banner.badges.length > 0 ? (
              <ul className="flex flex-wrap gap-2 mt-6" data-testid={`${slug}-hero-badges`}>
                {banner.badges.map((b, i) => {
                  const Ico = resolveIcon(b.icon);
                  return (
                    <li key={i} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-[12px] tracking-wide font-semibold text-white">
                      <Ico className="w-3.5 h-3.5 text-cyan-300" strokeWidth={2.25} />
                      {b.label}
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {(banner.cta_primary_label || banner.cta_secondary_label) ? (
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                {banner.cta_primary_label && banner.cta_primary_url ? (
                  <CtaLink href={banner.cta_primary_url} className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-xl bg-white text-[#082c50] font-bold tracking-tight text-base hover:bg-cyan-50 active:scale-[0.98] transition-all" testid={`${slug}-hero-cta-primary`}>
                    {banner.cta_primary_label}
                    <ArrowRight className="w-5 h-5" />
                  </CtaLink>
                ) : null}
                {banner.cta_secondary_label && banner.cta_secondary_url ? (
                  <CtaLink href={banner.cta_secondary_url} className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-xl bg-white/10 border-2 border-cyan-300/40 text-white hover:bg-white/15 font-semibold tracking-tight text-base transition-all backdrop-blur-sm" testid={`${slug}-hero-cta-secondary`}>
                    {banner.cta_secondary_label}
                  </CtaLink>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* ============ BREADCRUMBS ============ */}
      {breadcrumbs ? (
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <Breadcrumbs
              items={breadcrumbs}
              testid={`${slug}-breadcrumbs`}
            />
          </div>
        </div>
      ) : null}

      {/* ============ INTRO + CARDS ============ */}
      {(intro.title || cards.length > 0) && (
        <section className="bg-[#F8FAFC] py-16 sm:py-20 lg:py-24" data-testid={`${slug}-content`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {(intro.eyebrow || intro.title || intro.subtitle) ? (
              <div className="max-w-3xl mb-10">
                {intro.eyebrow ? (
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#1a5193] font-bold">{intro.eyebrow}</span>
                ) : null}
                {intro.title ? (
                  <h2 className="font-bold text-3xl sm:text-4xl tracking-tight text-[#082c50] mt-3">{intro.title}</h2>
                ) : null}
                {intro.subtitle ? (
                  <p className="mt-3 text-[#514e4e] leading-relaxed">{intro.subtitle}</p>
                ) : null}
              </div>
            ) : null}

            {cards.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid={`${slug}-cards`}>
                {cards.map((c, i) => {
                  const Icon = resolveIcon(c.icon);
                  const Wrapper = c.link_url ? Link : "div";
                  const wrapProps = c.link_url ? { to: c.link_url } : {};
                  return (
                    <Wrapper
                      key={c.id || i}
                      className={`group relative block h-full bg-white border border-slate-200 rounded-2xl p-6 ${c.link_url ? "hover:border-[#082c50]/40 hover:-translate-y-1 hover:shadow-[0_18px_38px_-22px_rgba(8,44,80,0.3)]" : ""} transition-all overflow-hidden`}
                      data-testid={`${slug}-card-${i}`}
                      {...wrapProps}
                    >
                      <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br ${CARD_GRADIENT} opacity-10 group-hover:opacity-20 transition-opacity`} />
                      <div className={`relative inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${CARD_GRADIENT} text-white`}>
                        <Icon className="w-6 h-6" strokeWidth={2} />
                      </div>
                      <h3 className="relative font-bold text-lg text-[#082c50] mt-4 tracking-tight">{c.title}</h3>
                      {c.description ? (
                        <p className="relative text-sm text-[#514e4e] mt-1.5 leading-relaxed">{c.description}</p>
                      ) : null}
                      {(c.badge || c.link_url) ? (
                        <div className="relative mt-4 flex items-center justify-between pt-3 border-t border-slate-100">
                          {c.badge ? (
                            <span className="text-[11px] uppercase tracking-[0.16em] text-[#1a5193] font-bold">{c.badge}</span>
                          ) : <span />}
                          {c.link_url ? (
                            <ArrowRight className="w-4 h-4 text-[#1a5193] group-hover:translate-x-1 transition-transform" />
                          ) : null}
                        </div>
                      ) : null}
                    </Wrapper>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ============ EXTRA SLOT (e.g. legacy quote form before CTA) ============ */}
      {children}

      {/* ============ CTA BAND ============ */}
      {(cta.title || cta.cta_primary_label) && (
        <section className="relative overflow-hidden bg-[#082c50] text-white py-16 sm:py-20" data-testid={`${slug}-cta-band`}>
          <div className="absolute inset-0">
            <div className="absolute -left-32 top-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-cyan-400/10 blur-3xl" />
            <div className="absolute right-0 bottom-0 w-[30rem] h-[30rem] rounded-full bg-[#1a5193]/30 blur-3xl" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1.2fr,0.8fr] gap-8 items-center">
            <div>
              {cta.title ? (
                <h3 className="font-bold text-3xl sm:text-4xl tracking-tight leading-tight">{cta.title}</h3>
              ) : null}
              {cta.description ? (
                <p className="mt-3 text-white/75 max-w-xl leading-relaxed">{cta.description}</p>
              ) : null}
            </div>
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
              {cta.cta_primary_label && cta.cta_primary_url ? (
                <CtaLink href={cta.cta_primary_url} className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-xl bg-white text-[#082c50] font-bold tracking-tight text-base hover:bg-cyan-50 transition-all" testid={`${slug}-cta-band-primary`}>
                  {cta.cta_primary_label}
                  <ArrowRight className="w-5 h-5" />
                </CtaLink>
              ) : null}
              {cta.cta_secondary_label && cta.cta_secondary_url ? (
                <CtaLink href={cta.cta_secondary_url} className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-xl bg-white/0 border border-white/30 text-white hover:bg-white/10 font-semibold tracking-tight text-base transition-all" testid={`${slug}-cta-band-secondary`}>
                  {cta.cta_secondary_label}
                </CtaLink>
              ) : null}
            </div>
          </div>
        </section>
      )}

      {bottomChildren}

      {/* SEO Sprint 3 — internal linking to destination pages (only for
          services where it semantically makes sense). */}
      {SERVICE_RELATED[slug] ? (
        <RelatedDestinations
          {...SERVICE_RELATED[slug]}
          testid={`${slug}-related-destinations`}
          variant="card"
        />
      ) : null}
    </div>
  );
}

function CtaLink({ href, className, testid, children }) {
  const isExternal = /^(https?:|tel:|mailto:|#)/.test(href || "");
  if (isExternal) {
    return <a href={href} className={className} data-testid={testid}>{children}</a>;
  }
  return <Link to={href || "#"} className={className} data-testid={testid}>{children}</Link>;
}
